import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, FileDown } from 'lucide-react';
import SubpageShell from './SubpageShell';
import SEO from '../components/SEO';
import '../styles/Academy.css';
import QualifiedEnquiryForm from '../components/QualifiedEnquiryForm';
import { downloadInfoPack } from '../components/academy/infoPackPdf';

const PACK_FIELDS = [
  { name: 'name', label: 'Your name', required: true, autoComplete: 'name' },
  { name: 'email', label: 'Work email', type: 'email', required: true, autoComplete: 'email' },
  { name: 'organisation', label: 'School or organisation', required: true, autoComplete: 'organization' },
  { name: 'role', label: 'Your role (optional)', autoComplete: 'organization-title' },
];

const InfoPack = () => (
  <section className="ac-info-pack" aria-labelledby="ac-pack-h" data-testid="school-info-pack">
    <div className="ac-info-pack-head">
      <span className="ac-info-pack-icon" aria-hidden="true"><FileDown size={22} strokeWidth={1.8} /></span>
      <div>
        <h2 id="ac-pack-h">Download the School Info Pack</h2>
        <p>A one-page summary of N3URO Academy to share with staff and governors: pathways, age levels, a sample session, sensory support and how to enquire.</p>
      </div>
    </div>
    <QualifiedEnquiryForm
      id="academy-info-pack"
      endpoint="/api/academy/info-pack-request"
      fields={PACK_FIELDS}
      submitLabel="Download the info pack (PDF)"
      successTitle="Your info pack is downloading."
      successBody="Feel free to share it with colleagues and governors."
      onSuccess={() => downloadInfoPack()}
      successAction={<button type="button" className="service-enquiry-submit" style={{ marginTop: '0.75rem' }} onClick={() => downloadInfoPack()} data-testid="academy-info-pack-download-again"><span>Download again</span> <FileDown size={16} /></button>}
    />
  </section>
);

const FIELDS = [
  { name: 'organisation', label: 'Organisation name', required: true, autoComplete: 'organization' },
  {
    name: 'orgType', label: 'Organisation type', type: 'select', required: true,
    options: [['school', 'School or college'], ['local-authority', 'Local authority'], ['charity', 'Charity or community group'],
      ['employer', 'Employer or workplace programme'], ['transport-aviation', 'Transport or aviation organisation'], ['other', 'Other']],
  },
  { name: 'contactName', label: 'Your name', required: true, autoComplete: 'name' },
  { name: 'role', label: 'Your role', required: true, autoComplete: 'organization-title' },
  { name: 'email', label: 'Work email', type: 'email', required: true, autoComplete: 'email' },
  { name: 'phone', label: 'Phone (optional)', type: 'tel', autoComplete: 'tel', maxLength: 40 },
  { name: 'region', label: 'Region or area (optional)' },
  {
    name: 'pathway', label: 'Pathway of interest', type: 'select', required: true,
    options: [['flight', 'Flight Academy'], ['rail', 'Rail Academy'], ['both', 'Both'], ['unsure', 'Not sure yet']],
  },
  { name: 'groupSize', label: 'Approximate number of children (optional)', maxLength: 40, placeholder: 'e.g. 12' },
  { name: 'message', label: 'What would you like to explore?', type: 'textarea', required: true, minLength: 10, wide: true },
];

const AcademyInstitutions = () => (
  <SubpageShell currentPath="/academy/institutions">
    <SEO
      title="N3URO Academy for schools & organisations"
      description="Bring N3URO Academy to your setting. Flight and Rail pathways are open now. Schools, local authorities, charities and organisations can register interest in group simulator workshops."
      path="/academy/institutions"
      image="https://n3uro.world/img/share-academy.jpg"
    />
    <main className="subpage-hero">
      <Link to="/academy" className="term-back" style={{ display: 'flex', width: 'fit-content', gap: '0.4rem', alignItems: 'center', color: '#5BE9FF', textDecoration: 'none', marginBottom: '1rem' }}>
        <ArrowLeft size={14} /> N3URO Academy
      </Link>
      <span className="subpage-eyebrow">For schools &amp; organisations</span>
      <h1 className="subpage-title">Bring N3URO Academy to your setting.</h1>
      <p className="subpage-lede">
        N3URO Academy&rsquo;s Flight and Rail pathways are open now. Our on-site simulator sessions run as
        selective 90-minute workshops, kept small with no more than 12&ndash;14 children per session and age group. Tell us about your
        organisation and we&rsquo;ll be in touch as soon as group dates open. This is an enquiry, not a booking,
        and no payment is taken.
      </p>
      <p className="service-enquiry-note" style={{ textAlign: 'left' }}>
        Enquiring for your own family? <Link to="/academy/register-interest" data-cta="academy-institutions-to-family">Register your family&rsquo;s interest instead</Link>.
      </p>
      <InfoPack />
      <section style={{ marginTop: '1.5rem' }}>
        <QualifiedEnquiryForm
          id="academy-institution"
          endpoint="/api/academy/institution-enquiry"
          title="Institutional enquiry"
          fields={FIELDS}
          submitLabel="Send institutional enquiry"
          successTitle="Thank you — your enquiry has been received."
          successBody="A member of the N3URO WORLD® team will review it and reply to your work email. No booking has been made."
        />
      </section>
    </main>
  </SubpageShell>
);

export default AcademyInstitutions;
